<template>
  <div>
    <!-- Barcode Preview Modal -->
    <app-custom-dialog
      v-model="dialog"
      :footer="false"
      title="barcode_preview"
      width="1000"
    >
      <div slot="activator">
        <v-row>
          <v-col cols="12">
            <v-row>
              <!-- Left: Barcode Preview (70%) -->
              <v-col
                cols="12"
                md="7"
                class="d-flex justify-center align-center"
              >
                <div class="barcode-preview-container">
                  <app-barcode :item="product" v-bind="barcodeSettings" />
                </div>
              </v-col>

              <!-- Right: Settings Controls (30%) -->
              <v-col cols="12" md="5">
                <v-card outlined class="settings-panel">
                  <!-- Quantity Control -->
                  <v-card-text class="pb-0 pt-2">
                    <div class="d-flex align-center mb-2">
                      <v-icon x-small color="primary" class="mr-1"
                        >fal fa-layer-group</v-icon
                      >
                      <span class="font-weight-medium text-caption">{{
                        $t("quantity") || "Quantity"
                      }}</span>
                    </div>
                    <div class="d-flex align-center justify-center mb-3">
                      <v-btn
                        fab
                        x-small
                        depressed
                        :disabled="quantity <= 1"
                        @click="quantity--"
                        color="error lighten-4"
                        class="error--text"
                      >
                        <v-icon x-small>fal fa-minus</v-icon>
                      </v-btn>
                      <v-text-field
                        v-model.number="quantity"
                        type="number"
                        :min="1"
                        dense
                        outlined
                        hide-details
                        class="mx-2 text-center quantity-input"
                        style="max-width: 70px"
                      />
                      <v-btn
                        fab
                        x-small
                        depressed
                        color="success lighten-4"
                        class="success--text"
                        @click="quantity++"
                      >
                        <v-icon x-small>fal fa-plus</v-icon>
                      </v-btn>
                    </div>
                  </v-card-text>

                  <v-divider class="my-1"></v-divider>

                  <!-- Collapsible Settings Panels -->
                  <v-expansion-panels
                    v-model="panels"
                    flat
                    accordion
                    multiple
                    class="expansion-panels-compact"
                  >
                    <!-- Section 1: Paper & Display -->
                    <v-expansion-panel>
                      <v-expansion-panel-header
                        class="py-2 px-2 min-height-auto"
                      >
                        <div class="d-flex align-center">
                          <v-icon x-small color="primary" class="mr-2"
                            >fal fa-cog</v-icon
                          >
                          <span class="font-weight-medium text-caption">{{
                            $t("paper_display") || "Paper & Display"
                          }}</span>
                        </div>
                        <template v-slot:actions="{ isExpanded }">
                          <v-icon x-small>{{
                            isExpanded
                              ? "fal fa-chevron-up"
                              : "fal fa-chevron-down"
                          }}</v-icon>
                        </template>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <!-- Paper Size -->
                        <v-card-text class="pa-0">
                          <div class="d-flex align-center mb-1">
                            <span class="text-caption grey--text">{{
                              $t("paper_size") || "Paper Size (mm)"
                            }}</span>
                          </div>
                          <v-row no-gutters>
                            <v-col cols="6" class="pr-1">
                              <v-text-field
                                v-model.number="settings.paperWidth"
                                :label="$t('width') || 'Width'"
                                type="number"
                                dense
                                outlined
                                hide-details
                                :min="20"
                                :max="500"
                                suffix="mm"
                              ></v-text-field>
                            </v-col>
                            <v-col cols="6" class="pl-1">
                              <v-text-field
                                v-model.number="settings.paperHeight"
                                :label="$t('height') || 'Height'"
                                type="number"
                                dense
                                outlined
                                hide-details
                                :min="20"
                                :max="500"
                                suffix="mm"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </v-card-text>

                        <!-- Display Options -->
                        <v-card-text class="pa-0">
                          <div class="d-flex align-center mb-1">
                            <span class="text-caption grey--text">{{
                              $t("display") || "Display"
                            }}</span>
                          </div>
                          <v-checkbox
                            v-model="settings.showName"
                            dense
                            hide-details
                            :label="$t('product_name') || 'Product Name'"
                            class="my-0"
                          ></v-checkbox>
                          <v-checkbox
                            v-model="settings.showPrice"
                            dense
                            hide-details
                            :label="$t('price') || 'Price'"
                            class="my-0"
                          ></v-checkbox>
                        </v-card-text>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Section 2: Barcode Size -->
                    <v-expansion-panel>
                      <v-expansion-panel-header
                        class="py-2 px-2 min-height-auto"
                      >
                        <div class="d-flex align-center">
                          <v-icon x-small color="primary" class="mr-2"
                            >fal fa-arrows-alt</v-icon
                          >
                          <span class="font-weight-medium text-caption">{{
                            $t("barcode_size") || "Barcode Size"
                          }}</span>
                        </div>
                        <template v-slot:actions="{ isExpanded }">
                          <v-icon x-small>{{
                            isExpanded
                              ? "fal fa-chevron-up"
                              : "fal fa-chevron-down"
                          }}</v-icon>
                        </template>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="pt-2">
                        <!-- Bar Width -->
                        <v-card-text class="pa-0">
                          <div class="d-flex align-center mb-1">
                            <span class="text-caption grey--text">{{
                              $t("bar_width") || "Bar Width (mm)"
                            }}</span>
                          </div>
                          <div class="d-flex align-center">
                            <v-slider
                              v-model="settings.width"
                              min="0.5"
                              max="10"
                              step="0.1"
                              dense
                              hide-details
                              class="flex-grow-1 mr-2"
                              thumb-size="14"
                            ></v-slider>
                            <v-text-field
                              v-model.number="settings.width"
                              type="number"
                              dense
                              outlined
                              hide-details
                              :min="0.5"
                              :max="10"
                              step="0.1"
                              style="max-width: 65px"
                            ></v-text-field>
                          </div>
                        </v-card-text>

                        <!-- Bar Height -->
                        <v-card-text class="pa-0">
                          <div class="d-flex align-center mb-1">
                            <span class="text-caption grey--text">{{
                              $t("bar_height") || "Bar Height (mm)"
                            }}</span>
                          </div>
                          <div class="d-flex align-center">
                            <v-slider
                              v-model="settings.height"
                              min="5"
                              max="100"
                              step="1"
                              dense
                              hide-details
                              class="flex-grow-1 mr-2"
                              thumb-size="14"
                            ></v-slider>
                            <v-text-field
                              v-model.number="settings.height"
                              type="number"
                              dense
                              outlined
                              hide-details
                              :min="5"
                              :max="100"
                              step="1"
                              style="max-width: 65px"
                            ></v-text-field>
                          </div>
                        </v-card-text>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Section 3: Font Settings -->
                    <v-expansion-panel>
                      <v-expansion-panel-header
                        class="py-2 px-2 min-height-auto"
                      >
                        <div class="d-flex align-center">
                          <v-icon x-small color="primary" class="mr-2"
                            >fal fa-text-height</v-icon
                          >
                          <span class="font-weight-medium text-caption">{{
                            $t("font_settings") || "Font Settings"
                          }}</span>
                        </div>
                        <template v-slot:actions="{ isExpanded }">
                          <v-icon x-small>{{
                            isExpanded
                              ? "fal fa-chevron-up"
                              : "fal fa-chevron-down"
                          }}</v-icon>
                        </template>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="pt-2">
                        <!-- Font Size -->
                        <v-card-text class="pa-0">
                          <div class="d-flex align-center mb-1">
                            <span class="text-caption grey--text">{{
                              $t("font_size_px") || "Font Size (px)"
                            }}</span>
                          </div>
                          <div class="d-flex align-center">
                            <v-slider
                              v-model="settings.fontSize"
                              min="8"
                              max="72"
                              dense
                              hide-details
                              class="flex-grow-1 mr-2"
                              thumb-size="14"
                            ></v-slider>
                            <v-text-field
                              v-model.number="settings.fontSize"
                              type="number"
                              dense
                              outlined
                              hide-details
                              :min="8"
                              :max="72"
                              style="max-width: 75px"
                            ></v-text-field>
                          </div>
                        </v-card-text>

                        <!-- Font Style -->
                        <v-card-text class="pa-0">
                          <div class="d-flex align-center mb-1">
                            <span class="text-caption grey--text">{{
                              $t("font_style") || "Font Style"
                            }}</span>
                          </div>
                          <v-chip-group
                            v-model="settings.fontOptions"
                            mandatory
                            column
                          >
                            <v-chip filter value="normal" x-small>{{
                              $t("normal") || "Normal"
                            }}</v-chip>
                            <v-chip filter value="bold" x-small>{{
                              $t("bold") || "Bold"
                            }}</v-chip>
                            <v-chip filter value="italic" x-small>{{
                              $t("italic") || "Italic"
                            }}</v-chip>
                          </v-chip-group>
                        </v-card-text>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card>
              </v-col>
            </v-row>

            <!-- Action Buttons -->
            <v-row class="mt-4">
              <v-col cols="12" class="d-flex justify-end">
                <v-btn text @click="close" class="mr-2">{{
                  $t("close") || "Close"
                }}</v-btn>
                <v-btn
                  color="primary"
                  @click="print"
                  :loading="printing"
                  depressed
                  class="rounded-lg"
                >
                  <v-icon left small>fal fa-print</v-icon>
                  {{ $t("print") || "Print" }}
                  <v-chip v-if="quantity > 1" x-small class="ml-2">{{
                    quantity
                  }}</v-chip>
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </div>
    </app-custom-dialog>

    <!-- Hidden Print Container -->
    <div
      v-if="product && Object.keys(product).length > 0"
      id="barcode-print-container"
      ref="printContainer"
      :key="`print-container-${quantity}`"
      style="display: none"
    >
      <div
        v-for="i in quantity"
        :key="`barcode-${i}-${quantity}`"
        :class="`barcode-print-item barcode-print-item-${i}`"
      >
        <app-barcode :item="product" v-bind="barcodeSettings" />
      </div>
    </div>
  </div>
</template>

<script>
import AppBarcode from "@/components/barcode";

export default {
  components: {
    AppBarcode,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dialog: false,
      quantity: 1,
      printing: false,
      // Expansion panels state (only paper & display open by default)
      panels: [0],
      settings: {
        // Typography
        fontSize: 15,
        fontOptions: "bold",
        textAlign: "center",
        textMargin: 5,
        displayValue: true,
        // Barcode dimensions (in mm)
        width: 2,
        height: 36,
        lineColor: "#000000",
        background: "#ffffff",
        // Margins (in mm)
        marginTop: 5,
        marginBottom: 5,
        marginLeft: 5,
        marginRight: 5,
        // Content display
        showName: true,
        showPrice: true,
        showSize: false,
        showCategory: false,
        showCode: false,
        // Barcode format
        format: "CODE128",
        // Paper size (in mm)
        paperWidth: 50, // mm
        paperHeight: 60, // mm (increased to fit name + price + barcode + text)
      },
    };
  },
  computed: {
    barcodeSettings() {
      return {
        ...this.settings,
        selected: [],
      };
    },
  },
  watch: {
    value(val) {
      this.dialog = val;
      if (val) {
        this.loadSettings();
      }
    },
    dialog(val) {
      this.$emit("input", val);
    },
    // Save settings when changed
    settings: {
      handler() {
        this.saveSettings();
      },
      deep: true,
    },
  },
  methods: {
    close() {
      this.dialog = false;
    },
    async print() {
      this.printing = true;
      try {
        // Validate product exists
        if (!this.product || !this.product.code) {
          throw new Error("Product is required for printing barcode");
        }

        // Validate quantity
        const quantity = Math.max(1, parseInt(this.quantity) || 1);

        // Force Vue to re-render the print container by updating the key
        // This ensures clean rendering without leftover content
        await this.$nextTick();

        // Get the print container
        const printContainer =
          this.$refs.printContainer ||
          document.getElementById("barcode-print-container");
        if (!printContainer) {
          throw new Error("Print container not found");
        }

        // Wait for Vue to finish rendering all barcode components
        // Multiple nextTick calls ensure async components (like vue-barcode) are rendered
        await this.$nextTick();
        await new Promise((resolve) => setTimeout(resolve, 200));

        // Get all barcode print items
        const printItems = printContainer.querySelectorAll(
          ".barcode-print-item"
        );
        const itemCount = printItems.length;

        // Validate item count
        if (itemCount !== quantity) {
          // Wait a bit more for async rendering
          await new Promise((resolve) => setTimeout(resolve, 300));
          const recheckItems = printContainer.querySelectorAll(
            ".barcode-print-item"
          );
          const recheckCount = recheckItems.length;

          if (recheckCount !== quantity) {
            throw new Error(
              `Failed to render correct number of barcodes. Expected ${quantity}, found ${recheckCount}`
            );
          }
        }

        // Clone the container to get clean HTML without Vue directives
        const clonedContainer = printContainer.cloneNode(true);

        // Remove any Vue-specific attributes and comments
        const cleanHTML = this.cleanVueHTML(clonedContainer.innerHTML);

        // Create a new window for printing
        const printWindow = window.open("", "_blank");
        if (!printWindow) {
          throw new Error("Unable to open print window");
        }

        // Calculate page dimensions
        const pageHeight = this.settings.paperHeight || 60;
        const pageWidth = this.settings.paperWidth || 50;
        const marginTop = this.settings.marginTop || 5;
        const marginBottom = this.settings.marginBottom || 5;
        const marginLeft = this.settings.marginLeft || 5;
        const marginRight = this.settings.marginRight || 5;
        const contentHeight = pageHeight - marginTop - marginBottom;

        // Build the print content with improved CSS
        const printContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Print Barcode - ${this.product.name || "Product"}</title>
            <meta charset="UTF-8">
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Lao+Looped:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
            <style>
              @page {
                size: ${pageWidth}mm ${pageHeight}mm;
                margin: 0;
              }
              * {
                box-sizing: border-box;
                margin: 0;
                padding: 0;
              }
              body {
                margin: 0;
                padding: 0;
                font-family: 'Noto Sans Lao Looped', 'Poppins', Arial, sans-serif;
                width: ${pageWidth}mm;
              }
              .barcode-print-item {
                width: ${pageWidth}mm;
                height: ${pageHeight}mm;
                page-break-after: avoid;
                page-break-inside: avoid;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: ${marginTop}mm ${marginLeft}mm ${marginBottom}mm ${marginRight}mm;
                background: ${this.settings.background || "#ffffff"};
                overflow: hidden;
                position: relative;
                box-sizing: border-box;
              }
              .barcode-print-item:not(:first-child) {
                page-break-before: always;
              }
              .barcode-label {
                text-align: center;
                font-size: ${this.settings.fontSize || 15}px;
                color: #000000;
                width: 100%;
                max-height: ${contentHeight}mm;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                box-sizing: border-box;
                overflow: hidden;
              }
              .barcode-text {
                font-family: 'Noto Sans Lao Looped', 'Poppins', Arial, sans-serif;
                line-height: 1.2;
                word-wrap: break-word;
                margin-bottom: ${this.settings.textMargin || 5}px;
                max-width: 100%;
              }
              .barcode-bold {
                font-weight: bold;
              }
              .barcode-italic {
                font-style: italic;
              }
              .barcode-svg {
                display: block;
                max-width: 100%;
                max-height: 100%;
                page-break-inside: avoid;
                flex-shrink: 0;
              }
              .barcode-svg svg {
                display: block;
                max-width: 100%;
                max-height: 100%;
                height: auto;
                width: auto;
                page-break-inside: avoid;
              }
            </style>
          </head>
          <body>
            ${cleanHTML}
          </body>
          </html>
        `;

        // Write content to the print window
        printWindow.document.write(printContent);
        printWindow.document.close();

        // Wait for content to load, then show print dialog
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            this.printing = false;
          }, 300);
        };

        // Emit print event for parent component handling
        this.$emit("print", {
          barcodes: Array(quantity).fill(this.product),
          settings: this.settings,
        });
      } catch (error) {
        console.error("[Barcode Print] Print error:", error);
        this.$toast.error(this.$t("print_error") || "Error printing barcode");
        this.printing = false;
      }
    },
    cleanVueHTML(html) {
      // Remove Vue-specific comment nodes and attributes
      // This helps ensure clean HTML output
      return html
        .replace(/<!--[\s\S]*?-->/g, "") // Remove HTML comments (Vue comment nodes)
        .replace(/\s+/g, " ") // Normalize whitespace
        .trim();
    },
    loadSettings() {
      try {
        const saved = localStorage.getItem("barcodeModalSettings");
        if (saved) {
          const settings = JSON.parse(saved);
          this.settings = { ...this.settings, ...settings };
        }
      } catch (error) {
        console.error("Error loading barcode settings:", error);
      }
    },
    saveSettings() {
      try {
        localStorage.setItem(
          "barcodeModalSettings",
          JSON.stringify(this.settings)
        );
      } catch (error) {
        console.error("Error saving barcode settings:", error);
      }
    },
  },
};
</script>

<style scoped>
.barcode-preview-container {
  padding: 24px;
  border: 1px dashed #ddd;
  border-radius: 8px;
  background: #fafafa;
  min-height: 450px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.settings-panel {
  max-height: 650px;
  overflow-y: auto;
}

.quantity-input >>> input {
  text-align: center;
}
</style>
